import clsx from "clsx";
import { useEffect, useState, type MouseEvent } from "react";
import {
	advanceHighlightQueue,
	emitter,
	enqueueHighlightCard,
	initialHighlightQueueState,
	type HighlightCardData,
	type HighlightQueueState,
} from "../util/highlightCard.ts";
import { SafeHtml } from "./SafeHtml.tsx";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import { useMobileBottomChrome } from "../hooks/useMobileBottomChrome.ts";

// No framer-motion here on purpose: its WAAPI tweens proved unreliable for
// these fixed overlays (entry animations freezing mid-tween, exit callbacks
// never firing → ghost DOM blocking touch input). Entry is a pure CSS keyframe
// animation applied on mount; exit is an exit class + a deterministic
// setTimeout that unmounts via React state. Keep it this way (see the G1a fix
// worklog entry before reintroducing any animation library here).
const HIGHLIGHT_TIMEOUT = 3000;
const EXIT_DURATION = 200;

// Buffer cards emitted before this component mounts (mirrors Notifications.tsx).
const initialHighlights: HighlightCardData[] = [];
const unbind = emitter.on("highlight", (card) => {
	initialHighlights.push(card);
});

export const MobileHighlightCard = () => {
	const reserveBottomChrome = useMobileBottomChrome();
	const [queue, setQueue] = useState<HighlightQueueState>(
		initialHighlightQueueState,
	);
	const [exiting, setExiting] = useState(false);

	useEffect(() => {
		unbind();

		const unbind2 = emitter.on("highlight", (card) => {
			setQueue((state) => enqueueHighlightCard(state, card));
		});

		for (const card of initialHighlights) {
			setQueue((state) => enqueueHighlightCard(state, card));
		}
		initialHighlights.length = 0;

		return unbind2;
	}, []);

	const current = queue.current;
	const currentId = current?.id;

	// Auto-dismiss: after the visible window, flip to the exit phase.
	useEffect(() => {
		if (currentId === undefined) {
			return;
		}
		const timeoutID = setTimeout(() => {
			setExiting(true);
		}, HIGHLIGHT_TIMEOUT);
		return () => {
			clearTimeout(timeoutID);
		};
	}, [currentId]);

	// Exit phase: once the CSS exit transition has had time to play, actually
	// unmount the card (React state → real DOM removal) and advance the queue.
	useEffect(() => {
		if (!exiting || currentId === undefined) {
			return;
		}
		const timeoutID = setTimeout(() => {
			setExiting(false);
			setQueue((state) => advanceHighlightQueue(state));
		}, EXIT_DURATION);
		return () => {
			clearTimeout(timeoutID);
		};
	}, [exiting, currentId]);

	const dismiss = () => {
		setExiting(true);
	};

	const handleClick = (event: MouseEvent<HTMLDivElement>) => {
		// If an inner link was tapped, the global router click delegate handles
		// navigation; otherwise navigate to the card's own url.
		const clickedLink = (event.target as HTMLElement).closest("a");
		if (!clickedLink && current?.url) {
			realtimeUpdate([], current.url);
		}
		dismiss();
	};

	if (!current) {
		return null;
	}

	return (
		<div
			className="mobile-highlight-card-container"
			data-bottom-chrome={reserveBottomChrome ? "reserved" : "inline"}
		>
			<div
				// Remount per card so the CSS entry animation replays for each one.
				key={current.id}
				className={clsx(
					"mobile-highlight-card",
					exiting && "mobile-highlight-card-exit",
				)}
				data-mobile-highlight-title={current.title}
				data-mobile-highlight-url={current.url}
				role="button"
				tabIndex={0}
				onClick={handleClick}
			>
				{current.imageUrl ? (
					<img
						className="mobile-highlight-card-avatar"
						src={current.imageUrl}
						alt=""
					/>
				) : (
					<div className="mobile-highlight-card-avatar mobile-highlight-card-avatar-empty" />
				)}
				<div className="mobile-highlight-card-body">
					{current.title ? (
						<div className="mobile-highlight-card-title">{current.title}</div>
					) : null}
					<div className="mobile-highlight-card-text">
						<SafeHtml dirty={current.message} htmlIsSafe={current.htmlIsSafe} />
					</div>
				</div>
			</div>
		</div>
	);
};
